import { useCallback, useState } from 'react';
import { getSupabase } from '@/lib/supabase';
import { env } from '@/config/env';
import { capture } from '@/modules/heat/signals';

export type CheckoutPlan = 'premium' | 'vip';

export const useCheckout = () => {
  const [busy, setBusy] = useState(false);

  const callFunction = useCallback(
    async (name: 'stripe-checkout' | 'stripe-portal', body?: unknown): Promise<{ url?: string; error?: string }> => {
      const supabase = getSupabase();
      if (!supabase) return { error: 'No connection.' };
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return { error: 'Not logged in.' };
      try {
        const res = await fetch(`${env.supabaseUrl}/functions/v1/${name}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: body ? JSON.stringify(body) : undefined,
        });
        const json = await res.json();
        if (!res.ok) return { error: json.error ?? `HTTP ${res.status}` };
        return { url: json.url };
      } catch (err) {
        return { error: err instanceof Error ? err.message : 'request failed' };
      }
    },
    [],
  );

  const startCheckout = useCallback(
    async (plan: CheckoutPlan): Promise<{ ok: boolean; error?: string }> => {
      setBusy(true);
      capture('surface_enter', 'plans', undefined, { event: 'checkout_started', plan });
      const result = await callFunction('stripe-checkout', { plan });
      setBusy(false);
      if (!result.url) return { ok: false, error: result.error };
      window.location.href = result.url;
      return { ok: true };
    },
    [callFunction],
  );

  const openPortal = useCallback(
    async (): Promise<{ ok: boolean; error?: string }> => {
      setBusy(true);
      const result = await callFunction('stripe-portal');
      setBusy(false);
      if (!result.url) return { ok: false, error: result.error };
      window.location.href = result.url;
      return { ok: true };
    },
    [callFunction],
  );

  return { startCheckout, openPortal, busy };
};
